import { type SortOrder } from "@common/declarations";
import { sort } from "@common/lib/array";
import { groupBy } from "lodash";
import { isSameValue } from "../lib";
import { type MultiSelectOptionItem, type MultiSelectOutputType } from "./component";

export type ValueType = string | number;

export const valueToArray = (
  options: MultiSelectOptionItem<ValueType>[],
  value: MultiSelectOptionItem<ValueType>[] | ValueType[] | string | undefined
): MultiSelectOptionItem<ValueType>[] => {
  if (!value) return [];
  if (typeof value === "string") {
    const values = value.split(",") as ValueType[];
    return options.filter((x) => values.some((y) => y == x.value));
  }
  if (!value.length) return [];
  if (typeof value[0] !== "object") {
    return options.filter((x) => value.some((y) => y == x.value));
  }

  return value as MultiSelectOptionItem<ValueType>[];
};

type ToOutputValueOptions = {
  allOptions: MultiSelectOptionItem<ValueType>[];
  initialValue: MultiSelectOptionItem<ValueType>[] | ValueType[] | string;
  options: MultiSelectOptionItem<ValueType>[];
  outputType: MultiSelectOutputType | undefined;
  uniqueValues: ValueType[] | undefined;
};
export const toOutputValue = (
  value: MultiSelectOptionItem<ValueType>[],
  options: ToOutputValueOptions
): MultiSelectOptionItem<ValueType>[] | ValueType[] | string => {
  const ivArray = options.initialValue
    ? typeof options.initialValue === "string"
      ? options.initialValue.split(",")
      : (options.initialValue as Array<ValueType | MultiSelectOptionItem<ValueType>>).map((x) =>
          typeof x === "object" ? x.value : x
        )
    : [];
  // add values that aren't present in options but in allOptions
  const allOptionsGroupped = groupBy(options.allOptions, (x) => x.value);
  const optionsGroupped = groupBy(options.options, (x) => x.value);
  let finalValue = [
    ...ivArray.filter((x) => !(x in optionsGroupped) && x in allOptionsGroupped).map((x) => allOptionsGroupped[x][0]),
    ...value
  ];
  if (options.uniqueValues) {
    const toRemove = finalValue.filter((x) => options.uniqueValues!.some((y) => isSameValue(y, x.value)));
    if (toRemove.length > 1) {
      const toKeep = toRemove.find((x) => !ivArray.some((y) => isSameValue(y, x.value)));
      finalValue = finalValue.filter((x) => !toRemove.some((y) => isSameValue(y.value, x.value) && x !== toKeep));
    }
  }
  switch (options.outputType) {
    case "array":
      return finalValue.map((x) => x.value);
    case "option":
      return finalValue;
    case "string":
      return finalValue.map((x) => x.value).join(",");
    default: {
      if (!options.initialValue || typeof options.initialValue === "string")
        return finalValue.map((x) => x.value).join(",");
      return finalValue.map((x) => x.value);
    }
  }
};

export const getBubbledOptions = (
  options: MultiSelectOptionItem<ValueType>[],
  value: ValueType[],
  sortOptions?: SortOrder,
  excludeOptionValues?: ValueType[]
) => {
  const selectedOptions = options.filter((x) => value.some((y) => y === x.value));
  const sortedSelectedList = sortOptions
    ? sort(selectedOptions, {
        key: "label",
        order: sortOptions,
        type: "natural"
      })
    : selectedOptions;
  const sortedOptions = (
    sortOptions
      ? sort(options, {
          key: "label",
          order: sortOptions,
          type: "natural"
        })
      : options
  ).filter((x) => !selectedOptions.some((y) => y.value === x.value));

  return [...sortedSelectedList, ...sortedOptions].filter((x) => !(excludeOptionValues ?? []).includes(x.value));
};
